import JSZip from 'jszip';
import { mediaAssets } from './MediaAssets';

export interface PressKitItem {
  name: string;
  url: string;
  category: string;
}

export class PressKitGenerator {
  private static instance: PressKitGenerator;

  public static getInstance(): PressKitGenerator {
    if (!PressKitGenerator.instance) {
      PressKitGenerator.instance = new PressKitGenerator();
    }
    return PressKitGenerator.instance;
  }

  async generatePressKit(): Promise<Blob> {
    const zip = new JSZip();

    // Create folder structure
    const logoFolder = zip.folder('logos');
    const iconsFolder = zip.folder('icons');
    const featuresFolder = zip.folder('features');
    const brandFolder = zip.folder('brand-elements');
    const screenshotsFolder = zip.folder('screenshots');

    // Add a README file
    const readmeContent = this.generateReadmeContent();
    zip.file('README.txt', readmeContent);

    try {
      // Process each media asset by category
      for (const asset of mediaAssets) {
        const targetFolder = this.getFolderForCategory(asset.category, {
          logoFolder,
          iconsFolder,
          featuresFolder,
          brandFolder,
          screenshotsFolder
        });

        // Add each variant of the asset
        for (const variant of asset.variants) {
          await this.addAssetToZip(targetFolder, asset.name, variant);
        }
      }

      // Generate the ZIP file
      return await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6
        }
      });
    } catch (error) {
      console.error('Error generating press kit:', error);
      throw new Error('Failed to generate press kit');
    }
  }

  private getFolderForCategory(category: string, folders: any) {
    switch (category.toLowerCase()) {
      case 'logo':
        return folders.logoFolder;
      case 'icon':
        return folders.iconsFolder;
      case 'feature':
        return folders.featuresFolder;
      case 'brand':
        return folders.brandFolder;
      case 'screenshot':
        return folders.screenshotsFolder;
      default:
        return folders.brandFolder; // Default to brand folder for 'other' category
    }
  }

  private async addAssetToZip(folder: any, assetName: string, variant: any) {
    try {
      // For now, we'll create placeholder files since actual assets need to be downloaded
      // In a real implementation, you would fetch the actual file content
      const fileName = `${assetName}_${variant.size || 'standard'}_${variant.background || 'default'}.${variant.format}`;
      
      // Create a placeholder file with metadata
      const metadata = {
        name: assetName,
        size: variant.size,
        format: variant.format,
        background: variant.background,
        url: variant.downloadUrl || `/assets/${fileName}`,
        description: variant.description || `${assetName} in ${variant.format} format`
      };

      folder.file(`${fileName}.metadata.json`, JSON.stringify(metadata, null, 2));
      
      // For actual implementation, you would do:
      // const response = await fetch(variant.downloadUrl);
      // const blob = await response.blob();
      // folder.file(fileName, blob);

    } catch (error) {
      console.warn(`Failed to add asset ${assetName}:`, error);
    }
  }

  private generateReadmeContent(): string {
    return `NEWSROOM PRESS KIT
==================

This press kit contains brand assets, logos, screenshots, and other media materials for Newsroom.

CONTENTS:
---------
• logos/          - Company logos in various formats and sizes
• icons/          - App icons and favicons
• features/       - Feature screenshots and graphics
• brand-elements/ - Brand colors, typography, and design elements
• screenshots/    - Application screenshots

USAGE GUIDELINES:
-----------------
• These assets are provided for media and press use only
• Please maintain proper spacing and proportions when using logos
• Do not modify, distort, or alter the brand assets
• For questions about usage, contact: hello@news-room.ca

LOGO VARIANTS:
--------------
• Light backgrounds: Use dark/colored logos
• Dark backgrounds: Use light/white logos
• Minimum size: 120px width for digital use

COMPANY INFORMATION:
--------------------
Company: Newsroom
Website: https://news-room.ca
Founded: 2021
Location: Victoria, BC, Canada
Industry: Financial Technology / AI-Powered Market Research

CONTACT:
--------
Press Contact: hello@news-room.ca
Phone: +1 (778) 751-0983

Generated on: ${new Date().toISOString()}
Press Kit Version: 1.0
`;
  }

  async downloadPressKit(): Promise<void> {
    try {
      const blob = await this.generatePressKit();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `newsroom-press-kit-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading press kit:', error);
      throw error;
    }
  }

  generateAssetList(): PressKitItem[] {
    const items: PressKitItem[] = [];

    for (const asset of mediaAssets) {
      for (const variant of asset.variants) {
        items.push({
          name: `${asset.name} (${variant.size || 'standard'}, ${variant.format})`,
          url: variant.downloadPath || `/assets/${asset.name.toLowerCase()}.${variant.format}`,
          category: asset.category
        });
      }
    }

    return items;
  }
}

export const pressKitGenerator = PressKitGenerator.getInstance();
